// NOTE: NSAI-5931 — the map contract has two halves, and confusing them is how
// a map ends up showing half its markers.
//
// 1. The SPEC (`IMapSpec`) is what the assistant writes: meaning only — where
//    things are and what they are called. It carries no zoom level, no centre,
//    no pixel and no tile URL. A model asked to pick a viewport in one shot
//    picks one that fits the first marker and loses the rest.
// 2. The VIEW (`IMapView`) is what the engine computes: a centre, a zoom, the
//    tiles to fetch and a screen position per marker, which web and mobile
//    render identically.
//
// Colours in the view are semantic ROLES, never literal hex, so the same view
// renders in the default skin or in an organisation's brand (NSAI-5932)
// without recomputing the geometry.

// NOTE: What a pin is. Reserved rather than used: the schema still accepts it
// so answers already stored keep rendering, but neither renderer draws it and
// the prompt no longer asks the model for it. A kind would never carry meaning
// on its own — every marker keeps a label — it would only say which shape and
// which role colour to paint, and giving it that job means drawing it on web
// and mobile before advertising it again.
export enum EMapMarkerKind {
  DEFAULT = "default",
  ORIGIN = "origin",
  DESTINATION = "destination",
  STOP = "stop",
  OFFICE = "office",
  WARNING = "warning",
  INCIDENT = "incident",
}

export enum EMapColorRole {
  SURFACE = "surface",
  INK = "ink",
  MUTED = "muted",
  FRAME = "frame",
  MARKER = "marker",
  MARKER_INK = "markerInk",
  MARKER_HALO = "markerHalo",
  FOCAL = "focal",
  FOCAL_INK = "focalInk",
  ROUTE = "route",
  AREA_FILL = "areaFill",
  AREA_STROKE = "areaStroke",
}

export type TMapTokens = Record<EMapColorRole, string>;

// NOTE: A coordinate pair as the model writes it: degrees, `lat` before `lon`,
// the order every mapping tool and every human uses when reading them aloud.
export interface IMapCoordinate {
  lat: number;
  lon: number;
}

export interface IMapMarker extends IMapCoordinate {
  id: string;
  label: string;
  sublabel?: string;
  kind?: EMapMarkerKind;
  // NOTE: What the map is ABOUT. Capped by the schema, because a map where
  // every pin is focal is a map where none of them is.
  focal?: boolean;
}

export interface IMapRoute {
  points: IMapCoordinate[];
  label?: string;
}

export interface IMapArea {
  points: IMapCoordinate[];
  label?: string;
}

export interface IMapSpec {
  markers: IMapMarker[];
  title?: string;
  subtitle?: string;
  route?: IMapRoute;
  area?: IMapArea;
}

export type TParseMapResult =
  | { success: true; data: IMapSpec }
  | { success: false; error: string };

// NOTE: A position in the unit square Web Mercator projects the world onto:
// x 0 at 180°W and 1 at 180°E, y 0 at the north edge and 1 at the south. Keeping
// the projection zoom-independent is what lets a zoom change reuse the same
// projected points instead of re-projecting every marker.
export interface IMapNormalizedPoint {
  x: number;
  y: number;
}

export interface IMapScreenPoint {
  x: number;
  y: number;
}

export interface IMapSize {
  width: number;
  height: number;
}

// NOTE: Where the map is looking. `center` is normalized rather than degrees so
// panning is plain arithmetic on the same numbers the tile grid is built from.
export interface IMapCamera {
  center: IMapNormalizedPoint;
  zoom: number;
}

export interface IMapTile {
  key: string;
  x: number;
  y: number;
  z: number;
  // NOTE: Where the tile's top-left corner lands in the viewport, in CSS
  // pixels. May be negative — a tile is normally half off-screen.
  left: number;
  top: number;
  size: number;
}

export interface IMapPlacedMarker {
  marker: IMapMarker;
  position: IMapScreenPoint;
}

export interface IMapView {
  camera: IMapCamera;
  size: IMapSize;
  tiles: IMapTile[];
  markers: IMapPlacedMarker[];
  route: IMapScreenPoint[];
  area: IMapScreenPoint[];
  accessibleTitle: string;
}

// NOTE: A resolved tile endpoint. `template` is null when this deployment has
// no usable one — an air-gapped installation, or a misconfigured URL — which
// the renderers answer by dropping the basemap and showing the marker list.
export interface IMapTileSource {
  template: string | null;
  attribution: string;
}

// NOTE: What each client's tile-settings hook answers, read from /app-config.
// `isPending` is true only until the first answer arrives: the figure holds its
// placeholder until then, rather than announcing "no basemap" for the moment it
// takes to load and then swapping a map in. A failed request is not pending,
// and leaves the basemap off.
export interface IMapTileConfig {
  tileSource: IMapTileSource;
  maxZoom: number;
  isPending: boolean;
}

// NOTE: The colours one render paints with, plus the mode they were resolved
// for. Both clients hold exactly this and pass it down, so a component that
// takes a skin behaves the same wherever it is mounted.
export interface IMapSkin {
  tokens: TMapTokens;
  isDark: boolean;
}

// NOTE: The prop shapes the two renderers share. They name nothing
// platform-specific — a spec, a computed view, the resolved tokens and which
// marker is selected — so a copy per client was a copy of the same thing, and
// the pair could drift into taking different props for the same figure. What
// stays in each app's own `types.ts` is what genuinely differs there: the tile
// layer takes a grid on web and the whole view on mobile, and only web has a
// callout and a pannable canvas.
export interface IMapMarkerSelection {
  markerId: string;
}

export interface IMapBlockProps {
  rawJson: string;
  isStreaming?: boolean;
}

// NOTE: A block that broke the contract: why, and its own source to show.
export interface IMapInvalidSpecProps {
  reason: string;
  rawJson: string;
}

export interface IMapFigureProps {
  spec: IMapSpec;
}

export interface IMapOverlayProps {
  view: IMapView;
  tokens: TMapTokens;
  activeMarkerId: string | null;
  onMarkerSelect: (props: IMapMarkerSelection) => void;
}

export interface IMapMarkerPinProps {
  placed: IMapPlacedMarker;
  index: number;
  tokens: TMapTokens;
  isActive: boolean;
  onSelect: (props: IMapMarkerSelection) => void;
}

// NOTE: The two shapes a spec can carry besides its pins. A labelled one gets a
// legend row of its own, so an "Evacuation route" is shown and read out rather
// than drawn as an anonymous line.
export enum EMapLegendShape {
  ROUTE = "route",
  AREA = "area",
}

export interface IMapLegendShapeProps {
  shape: EMapLegendShape;
  label: string;
  tokens: TMapTokens;
}

export interface IMapLegendProps {
  spec: IMapSpec;
  tokens: TMapTokens;
  activeMarkerId: string | null;
  onMarkerSelect: (props: IMapMarkerSelection) => void;
}
